import clsx from "clsx";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import GhostButton from "./GhostButton";
import { RowSpinner } from "./Loader/Loader";
import DocIcon from "./icons/DocIcon";
import PrimaryButton from "./PrimaryButton";

const FRAME = {
  processing: "border-[#e2e2e2]",
  recognized: "border-[#e2e2e2]",
  unrecognized: "border-[#f0c2c2]",
  invalid: "border-[#f0c2c2]",
  "too-large": "border-[#f0d6a8]",
  "rate-limited": "border-[#f0d6a8]",
  transport: "border-[#f0d6a8]",
};

// The report row that lies on an account (the mock): a muted tile without a border, not a card in a card.
const IN_SYNC_FRAME = {
  "in-sync": "rounded-xl px-3 py-2.5 bg-surface-muted",
  "in-sync-failed": "rounded-xl px-3 py-2.5 bg-surface-muted border border-danger-border",
};

// A report that already lies on an account (AC-01b) and its danger variant — the report that broke preparation (AC-06).
const IN_SYNC = new Set(["in-sync", "in-sync-failed"]);

const DANGER = new Set(["unrecognized", "invalid"]);
const RETRYABLE = new Set(["rate-limited", "transport"]);
const REMOVABLE = new Set(["unrecognized", "invalid", "too-large", "rate-limited", "transport"]);

function StateIcon({ state }) {
  if (state === "processing") return <RowSpinner />;
  if (IN_SYNC.has(state)) {
    return (
      <span className="inline-flex shrink-0">
        <DocIcon color={state === "in-sync-failed" ? "var(--danger)" : undefined} />
      </span>
    );
  }
  if (state === "recognized") {
    return (
      <span aria-hidden="true" className="inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full bg-green-450 text-white text-xs">
        ✓
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={clsx(
        "inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full text-white text-xs font-semibold",
        DANGER.has(state) ? "bg-[#c23b3b]" : "bg-[#b8790f]",
      )}
    >
      !
    </span>
  );
}

function FileRow({
  fileName,
  state,
  meta,
  message,
  queued = false,
  removingReport = false,
  removeReportBlocked = false,
  disabled = false,
  onRemoveReport,
  onRemove,
  onRetry,
}) {
  const { t } = useTranslation();
  const nameId = useId();

  let reason = null;
  if (state === "processing") reason = t(queued ? "unifiedUpload.row.queued" : "unifiedUpload.row.processing");
  else if (state === "invalid") reason = message;
  else if (state !== "recognized" && !IN_SYNC.has(state)) reason = t(`unifiedUpload.row.${state}`);

  return (
    <li
      aria-labelledby={nameId}
      data-state={state}
      className={clsx(
        "flex flex-col md:flex-row md:items-center gap-3",
        IN_SYNC.has(state) ? IN_SYNC_FRAME[state] : ["rounded-2xl border bg-white px-4 py-3", FRAME[state]],
      )}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <StateIcon state={state} />
        <div className="min-w-0">
          <p id={nameId} className="text-sm font-medium truncate" title={fileName}>
            {fileName}
          </p>
          {state === "recognized" && (
            <p className={clsx("text-xs mt-0.5", removeReportBlocked ? "text-[#c23b3b]" : "text-[#8c8c8c]")}>
              {removeReportBlocked ? t("unifiedUpload.row.removeReportBlocked", { meta }) : meta}
            </p>
          )}
          {IN_SYNC.has(state) && (
            <p className={clsx("text-xs mt-0.5", state === "in-sync-failed" ? "text-danger" : "text-text-muted")}>
              {meta}
              {state === "in-sync-failed" && ` · ${t("syncUnits.report.failed")}`}
            </p>
          )}
          {reason && (
            <p
              data-testid="file-row-reason"
              aria-live="polite"
              className={clsx(
                "text-xs mt-0.5",
                state === "processing" ? "text-[#8c8c8c]" : DANGER.has(state) ? "text-[#c23b3b]" : "text-[#b8790f]",
              )}
            >
              {reason}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {state === "recognized" && !removeReportBlocked && (
          <GhostButton onClick={onRemoveReport} disabled={disabled || removingReport}>
            {t(removingReport ? "unifiedUpload.row.removingReport" : "unifiedUpload.row.removeReport")}
          </GhostButton>
        )}
        {RETRYABLE.has(state) && (
          <PrimaryButton size="sm" onClick={onRetry}>
            {t("unifiedUpload.row.retry")}
          </PrimaryButton>
        )}
        {REMOVABLE.has(state) && <GhostButton onClick={onRemove}>{t("unifiedUpload.row.remove")}</GhostButton>}
      </div>
    </li>
  );
}

export default FileRow;
